import { Badge, Icon, Money } from "@/components";
import { formatInteger, MISSING_VALUE, pluralize } from "@/lib/format";
import { countryName } from "@/lib/regions";
import type { MarketOverview as Overview } from "../api";
import { perUnitLabel, unitPriceDecimals, unitSymbol } from "../format";
import styles from "./MarketOverview.module.css";

interface MarketOverviewProps {
  overview: Overview;
  currency: string;
  /** The ship-to country, for "Doesn’t ship to Canada". */
  country: string;
  onToggleSupplier: (slug: string) => void;
}

/** Who sells what the search found: each supplier's matches, stock and lowest prices, and the range of
 * unit prices. A supplier's name toggles the search's supplier filter. */
export function MarketOverview({ overview, currency, country, onToggleSupplier }: MarketOverviewProps) {
  if (overview.suppliers.length === 0) return null;
  return (
    <section className={styles.overview} aria-labelledby="supplies-who-sells-it">
      <div className={styles.summary}>
        <h2 id="supplies-who-sells-it" className={styles.title}>
          Who sells it
        </h2>
        {overview.unit_price_ranges.map((range) => {
          const decimals = unitPriceDecimals(range.basis);
          return (
            <p key={range.basis} className={styles.range}>
              {perUnitLabel(range.basis)}:{" "}
              <Money minor={range.min_minor} currency={currency} extraDecimals={decimals} /> to{" "}
              <Money minor={range.max_minor} currency={currency} extraDecimals={decimals} />, median{" "}
              <Money minor={range.median_minor} currency={currency} extraDecimals={decimals} /> across{" "}
              {pluralize(range.priced, "priced offer", "priced offers")}
            </p>
          );
        })}
      </div>
      <table className={styles.table}>
        <caption className={styles.caption}>Select a supplier to show only its supplies.</caption>
        <thead>
          <tr>
            <th scope="col">Supplier</th>
            <th scope="col" className={styles.num}>
              Matches
            </th>
            <th scope="col" className={styles.num}>
              In stock
            </th>
            <th scope="col" className={styles.num}>
              Lowest unit price
            </th>
            <th scope="col" className={styles.num}>
              Lowest pack price
            </th>
          </tr>
        </thead>
        <tbody>
          {overview.suppliers.map((entry) => (
            <tr key={entry.supplier.id}>
              <th scope="row" className={styles.supplierCell}>
                <button
                  type="button"
                  className={styles.supplier}
                  aria-pressed={entry.selected}
                  onClick={() => onToggleSupplier(entry.supplier.slug)}
                >
                  {entry.selected ? <Icon name="check" /> : null}
                  {entry.supplier.name}
                </button>
                {entry.supplier.ships_to_workspace === false ? (
                  <Badge tone="warning">Doesn’t ship to {countryName(country)}</Badge>
                ) : null}
              </th>
              <td className={styles.num}>{formatInteger(entry.count)}</td>
              <td className={styles.num}>{formatInteger(entry.in_stock)}</td>
              <td className={styles.num}>
                {entry.lowest_unit_prices.length
                  ? entry.lowest_unit_prices.map((unit) => (
                      <Money
                        key={unit.basis}
                        minor={unit.minor}
                        currency={currency}
                        extraDecimals={unitPriceDecimals(unit.basis)}
                        per={unitSymbol(unit.basis)}
                        className={styles.unitPrice}
                      />
                    ))
                  : MISSING_VALUE}
              </td>
              <td className={styles.num}>
                {entry.lowest_price_minor !== null ? (
                  <Money minor={entry.lowest_price_minor} currency={currency} />
                ) : (
                  MISSING_VALUE
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
